import { useEffect, useRef, ReactNode, memo } from 'react'
import {
  buildGoogleFontsUrl,
} from '@/lib/fonts'

interface FontLoaderProps {
  fonts: string[] // Array of font IDs to load
  children: ReactNode
}

// In-memory cache for loaded fonts (persists across re-renders)
const loadedFontsCache = new Set<string>()

/**
 * FontLoader component that dynamically injects Google Fonts link tags
 * into the document head. Uses a cache to prevent duplicate loading.
 */
export const FontLoader = memo(function FontLoader({ fonts, children }: FontLoaderProps) {
  const loadedRef = useRef<boolean>(false)

  useEffect(() => {
    // Filter out already loaded fonts
    const fontsToLoad = fonts.filter(fontId => !loadedFontsCache.has(fontId))

    if (fontsToLoad.length === 0) {
      return
    }

    // Build the Google Fonts URL
    const fontsUrl = buildGoogleFontsUrl(fontsToLoad)

    if (!fontsUrl) {
      return
    }

    // Create and inject the link element
    const linkId = `google-fonts-${fontsToLoad.join('-')}`
    const existingLink = document.getElementById(linkId)

    if (existingLink) {
      return
    }

    const link = document.createElement('link')
    link.id = linkId
    link.rel = 'stylesheet'
    link.href = fontsUrl

    // Append to head
    document.head.appendChild(link)

    // Mark fonts as loaded in cache
    fontsToLoad.forEach(fontId => loadedFontsCache.add(fontId))
  }, [fonts])

  // Mark as loaded on initial render (for conditional rendering safety)
  useEffect(() => {
    loadedRef.current = true
  }, [])

  return <>{children}</>
})

/**
 * Check if a font is already loaded
 */
export function isFontLoaded(fontId: string): boolean {
  return loadedFontsCache.has(fontId)
}

/**
 * Get all currently loaded fonts
 */
export function getLoadedFonts(): string[] {
  return Array.from(loadedFontsCache)
}

/**
 * Reset the font cache (mainly for testing)
 */
export function resetFontCache(): void {
  loadedFontsCache.clear()
}

/**
 * Load fonts imperatively (useful for non-React contexts)
 */
export function loadFonts(fontIds: string[]): void {
  const fontsToLoad = fontIds.filter(fontId => !loadedFontsCache.has(fontId))

  if (fontsToLoad.length === 0) {
    return
  }

  const fontsUrl = buildGoogleFontsUrl(fontsToLoad)

  if (!fontsUrl) {
    return
  }

  const linkId = `google-fonts-${fontsToLoad.join('-')}`

  if (document.getElementById(linkId)) {
    return
  }

  const link = document.createElement('link')
  link.id = linkId
  link.rel = 'stylesheet'
  link.href = fontsUrl

  document.head.appendChild(link)

  fontsToLoad.forEach(fontId => loadedFontsCache.add(fontId))
}
